import React, { useRef, useState, useEffect } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import image1 from '../assets/phone-mockups/Exclusive.jpg';
import image2 from '../assets/phone-mockups/CampusFeed.jpg';
import image3 from '../assets/phone-mockups/GlobalFeed.jpg';
import image4 from '../assets/phone-mockups/1on1and4on4.png';
import image5 from '../assets/phone-mockups/Customrooms.png';
import image6 from '../assets/phone-mockups/AnonSetup.jpg';
import image7 from '../assets/phone-mockups/Match.jpg';
import image8 from '../assets/phone-mockups/AnonChat.jpg';

const demoScreens = [
  { id: 1, title: 'Verified Network', description: 'Exclusively for college students.', image: image1 },
  { id: 2, title: 'Campus Feed', description: 'Discover your campus\'s only network.', image: image2 },
  { id: 3, title: 'Global Feed', description: 'See college life worldwide. Global POV and campus stories.', image: image3 },
  { id: 4, title: '1-1 & 4-4 Rooms', description: 'Connect instantly with high-quality audio. Drop into spontaneous 4-person rooms or jump globally.', image: image4 },
  { id: 5, title: 'Custom Voice Rooms', description: 'Create private, password-protected 8-person audio networks and share invite links directly with friends.', image: image5 },
  { id: 6, title: 'Anonymous Chat', description: 'Choose to connect locally on campus or go global.', image: image6 },
  { id: 7, title: 'Match Activated', description: 'Find your random slay in a low-pressure zone.', image: image7 },
  { id: 8, title: 'Chat Anonymously', description: 'Converse freely with no filters. Start a high-vibe conversation.', image: image8 },
];

export const PhoneDemo = () => {
  const containerRef = useRef(null);
  
  // Track scroll progress within this huge 600vh container
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start start', 'end end']
  });

  // --- Cinematic Transforms ---
  // Entrance (0 to 0.15): Phone starts translated down, to the right, scaled down, rotated.
  // Scrubbing (0.15 to 0.85): Phone stays locked in place.
  // Exit (0.85 to 1): Phone scales down and slides up.
  
  const phoneX = useTransform(scrollYProgress, [0, 0.15, 0.85, 1], ["50vw", "0vw", "0vw", "-50vw"]);
  const phoneY = useTransform(scrollYProgress, [0, 0.15, 0.85, 1], ["30vh", "0vh", "0vh", "-30vh"]);
  const phoneScale = useTransform(scrollYProgress, [0, 0.15, 0.85, 1], [0.7, 1, 1, 0.8]);
  const phoneRotateZ = useTransform(scrollYProgress, [0, 0.15, 0.85, 1], [15, 0, 0, -15]);
  const phoneRotateY = useTransform(scrollYProgress, [0, 0.15, 0.85, 1], [45, 5, -5, -45]);
  const phoneRotateX = useTransform(scrollYProgress, [0, 0.15, 0.85, 1], [25, 2, 2, 25]);

  // Spotlight Background Overlay (darkens everything else)
  const spotlightOpacity = useTransform(scrollYProgress, [0, 0.15, 0.85, 1], [0, 1, 1, 0]);
  
  // Text Opacity (fades in as phone centers)
  const textOpacity = useTransform(scrollYProgress, [0.05, 0.15, 0.85, 0.95], [0, 1, 1, 0]);

  // Physical Glass Reflection sweeping across the phone
  const reflectionY = useTransform(scrollYProgress, [0, 1], ["-100%", "100%"]);

  const [currentScreen, setCurrentScreen] = useState(0);
  const [direction, setDirection] = useState(1);

  // Map scroll progress (0.15 -> 0.85) to the 8 screens
  useEffect(() => {
    const unsubscribe = scrollYProgress.on('change', (v) => {
      if (v < 0.15) {
        if (currentScreen !== 0) {
          setDirection(-1);
          setCurrentScreen(0);
        }
      } else if (v > 0.85) {
        if (currentScreen !== demoScreens.length - 1) {
          setDirection(1);
          setCurrentScreen(demoScreens.length - 1);
        }
      } else {
        // We are between 0.15 and 0.85
        // Progress within this scrubbing zone goes from 0.0 to 1.0
        const scrubProgress = (v - 0.15) / 0.70;
        const index = Math.min(
          demoScreens.length - 1,
          Math.max(0, Math.floor(scrubProgress * demoScreens.length))
        );
        
        if (index !== currentScreen) {
          setDirection(index > currentScreen ? 1 : -1);
          setCurrentScreen(index);
        }
      }
    });
    return () => unsubscribe();
  }, [scrollYProgress, currentScreen]);

  return (
    <section ref={containerRef} className="relative h-[600vh] bg-sp-bg">
      {/* Sticky Container locks to the screen for the duration of the 600vh scroll */}
      <div className="sticky top-0 h-screen flex flex-col items-center justify-center overflow-hidden px-4 perspective-[1000px]">
        
        {/* Spotlight Background Overlay */}
        <motion.div 
          className="absolute inset-0 bg-[#020304]/90 backdrop-blur-sm z-0"
          style={{ opacity: spotlightOpacity }}
        />

        <motion.div className="relative z-10 w-full max-w-6xl flex flex-col md:flex-row items-center justify-center gap-12 lg:gap-24">
          
          {/* Left Side: Dynamic Text (Only visible during scrubbing phase) */}
          <motion.div 
            className="hidden md:flex flex-col w-1/3 text-left"
            style={{ opacity: textOpacity }}
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={currentScreen}
                initial={{ opacity: 0, y: 30, filter: 'blur(8px)' }}
                animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                exit={{ opacity: 0, y: -30, filter: 'blur(8px)' }}
                transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              >
                <h3 className="text-3xl lg:text-4xl font-medium tracking-tight text-sp-cyan mb-4 drop-shadow-[0_0_8px_var(--sp-glow)]">
                  {demoScreens[currentScreen].title}
                </h3>
                <p className="text-xl text-sp-text-secondary leading-relaxed font-light">
                  {demoScreens[currentScreen].description}
                </p>
              </motion.div>
            </AnimatePresence>
            
            {/* Scroll Progress Dots */}
            <div className="flex gap-2 mt-12">
              {demoScreens.map((_, idx) => (
                <div 
                  key={idx}
                  className={`h-1.5 rounded-full transition-all duration-300 ${idx === currentScreen ? 'w-8 bg-sp-cyan shadow-[0_0_10px_var(--sp-glow)]' : 'w-2 bg-gray-800'}`}
                />
              ))}
            </div>
          </motion.div>

          {/* Center/Right: Cinematic Phone Mockup */}
          <motion.div 
            className="relative w-72 lg:w-80 h-[550px] lg:h-[600px] shrink-0 z-20 group"
            style={{
              x: phoneX,
              y: phoneY,
              scale: phoneScale,
              rotateZ: phoneRotateZ,
              rotateY: phoneRotateY,
              rotateX: phoneRotateX,
              transformStyle: "preserve-3d"
            }}
          >
            {/* The Phone Hardware Frame */}
            <div className="absolute inset-0 rounded-[40px] bg-[#050505] border-[14px] border-[#111] shadow-[0_30px_60px_rgba(0,0,0,0.8)] overflow-hidden">
              <div className="relative w-full h-full overflow-hidden bg-black">
                {/* Physical Glass Screen Reflection Overlay */}
                <div className="absolute inset-0 z-50 pointer-events-none bg-gradient-to-tr from-white/0 via-white/5 to-white/0 mix-blend-overlay" />
                <motion.div 
                  className="absolute top-[-50%] left-[-50%] w-[200%] h-[200%] rotate-45 bg-gradient-to-b from-white/10 to-transparent pointer-events-none z-50"
                  style={{ y: reflectionY }}
                />
                
                <AnimatePresence mode="wait" custom={direction}>
                  <motion.div
                    key={currentScreen}
                    custom={direction}
                    initial={{ y: direction > 0 ? 100 : -100, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: direction > 0 ? -100 : 100, opacity: 0 }}
                    transition={{ duration: 0.4, ease: 'easeOut' }}
                    className="absolute inset-0"
                  >
                    <img
                      src={demoScreens[currentScreen].image}
                      alt={demoScreens[currentScreen].title}
                      className="w-full h-full object-cover"
                    />
                    {/* Mobile text overlay (only visible on small screens) */}
                    <div className="md:hidden absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/90 to-transparent">
                      <h3 className="text-sp-text text-xl font-medium tracking-tight mb-2">
                        {demoScreens[currentScreen].title}
                      </h3>
                      <p className="text-sp-text-secondary text-sm">
                        {demoScreens[currentScreen].description}
                      </p>
                    </div>
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
            
            {/* Cinematic Cyan Glow behind phone, locked to the phone's movement */}
            <motion.div 
              className="absolute inset-0 rounded-[40px] pointer-events-none"
              style={{
                boxShadow: "0 0 100px var(--sp-glow)",
                opacity: spotlightOpacity
              }}
            />
          </motion.div>

        </motion.div>
      </div>
    </section>
  );
};

export default PhoneDemo;
